import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Droplets, Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function MorningCleanseGuide() {
  const navigate = useNavigate();

  const steps = [
    { step: 1, title: "Wash Your Hands First", desc: "Always start with clean hands to avoid transferring bacteria to your face", time: "15 sec" },
    { step: 2, title: "Wet Your Face", desc: "Use lukewarm water - hot water strips natural oils, cold doesn't open pores", time: "15 sec" },
    { step: 3, title: "Apply Cleanser", desc: "Use a dime-sized amount of your morning cleanser", time: "5 sec" },
    { step: 4, title: "Massage in Circles", desc: "Gently massage in upward circular motions for a full 60 seconds", time: "60 sec" },
    { step: 5, title: "Don't Forget Hidden Areas", desc: "Pay attention to hairline, nose creases, jawline, and behind ears", time: "30 sec" },
    { step: 6, title: "Rinse Thoroughly", desc: "Splash 15-20 times with lukewarm water to remove all cleanser", time: "30 sec" },
    { step: 7, title: "Pat Dry Gently", desc: "Use a clean, soft towel. Pat, don't rub - rubbing causes irritation", time: "15 sec" }
  ];

  const cleanserTypes = [
    { type: "Gel Cleansers", best: "Oily/Combination skin", note: "Deep cleaning without over-drying" },
    { type: "Cream Cleansers", best: "Dry/Mature skin", note: "Hydrating and gentle" },
    { type: "Foaming Cleansers", best: "Normal/Oily skin", note: "Light and refreshing" },
    { type: "Micellar Water", best: "Sensitive/All types", note: "Gentle, no-rinse option" },
    { type: "Oil Cleansers", best: "Makeup removal/PM", note: "Dissolves oil-based products" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-sky-100 text-sky-700 rounded-full text-sm font-semibold mb-4">
            Week 3 • Day 2 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Morning Cleanse Ritual
          </h1>
          <p className="text-xl text-secondary-600">Start Your Day with a Fresh, Clean Canvas</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-sky-500 to-cyan-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Droplets className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">The 60-Second Rule</h3>
              <p className="text-sky-100 text-lg">
                Most people cleanse for only 10-15 seconds. Your cleanser needs a full 60 seconds on your skin to properly break down oils and impurities.
              </p>
            </div>
          </div>
        </section>

        {/* Steps */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <Clock className="w-6 h-6 text-sky-500" />
            The Perfect Cleanse (Step by Step)
          </h2>
          <div className="space-y-4">
            {steps.map((item) => (
              <div key={item.step} className="flex items-start gap-4 p-4 bg-sky-50 rounded-xl">
                <div className="w-10 h-10 bg-sky-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  {item.step}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <h3 className="font-bold text-secondary-900">{item.title}</h3>
                    <span className="text-xs bg-sky-200 text-sky-700 px-2 py-1 rounded-full">{item.time}</span>
                  </div>
                  <p className="text-secondary-600 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Cleanser Types */}
        <section className="bg-gradient-to-br from-sky-50 to-cyan-50 rounded-2xl p-8 border-2 border-sky-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Choose Your Cleanser</h2>
          <div className="space-y-3">
            {cleanserTypes.map((cleanser, index) => (
              <div key={index} className="bg-white rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                <h3 className="font-bold text-sky-700 md:w-1/4">{cleanser.type}</h3>
                <span className="text-sm bg-sky-100 text-sky-600 px-3 py-1 rounded-full md:w-1/4">Best for: {cleanser.best}</span>
                <p className="text-sm text-secondary-600 md:flex-1">{cleanser.note}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Common Mistakes */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            Common Mistakes to Avoid
          </h2>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              "Using water that's too hot",
              "Rushing (cleansing for less than 60 seconds)",
              "Scrubbing too hard",
              "Using the same towel all week",
              "Skipping the hairline and jawline",
              "Using harsh or stripping cleansers"
            ].map((mistake, i) => (
              <div key={i} className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg text-secondary-700">
                <CheckCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                {mistake}
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default MorningCleanseGuide;
